import { create } from 'zustand';

export type AppState =
  | 'landing'
  | 'legal-gate'
  | 'permission'
  | 'permission-denied'
  | 'calibration'
  | 'queue'
  | 'queue-timeout'
  | 'connecting'
  | 'dueling'
  | 'resolving'
  | 'resolution'
  | 'share'
  | 'report-flow'
  | 'peer-dropped'
  | 'solo-scan'
  | 'solo-smile-scan'
  | 'solo-frown-scan'
  | 'solo-anger-scan'
  | 'solo-surprise-scan'
  | 'solo-wink-scan'
  | 'solo-disgust-scan'
  | 'solo-resting-face-scan'
  | 'solo-stillness-challenge'
  | 'solo-nod-challenge'
  | 'solo-shake-challenge'
  | 'solo-blink-challenge';

export type AppEvent =
  | 'ENTER'
  | 'CONSENT_OK'
  | 'PERMISSION_GRANTED'
  | 'CAMERA_DENIED'
  | 'WARMUP_DONE'
  | 'QUEUE_START'
  | 'QUEUE_TIMEOUT'
  | 'MATCH_FOUND'
  | 'CONNECTED'
  | 'WS_DROPPED'
  | 'PEER_LEFT'
  | 'MATCH_END'
  | 'RESOLVED'
  | 'NEXT'
  | 'BACK_TO_LANDING'
  | 'REPORT'
  | 'POW_FAIL'
  | 'SOLO_SCAN'
  | 'SOLO_SMILE_SCAN'
  | 'SOLO_FROWN_SCAN'
  | 'SOLO_ANGER_SCAN'
  | 'SOLO_SURPRISE_SCAN'
  | 'SOLO_WINK_SCAN'
  | 'SOLO_DISGUST_SCAN'
  | 'SOLO_RESTING_FACE_SCAN'
  | 'SOLO_STILLNESS_CHALLENGE'
  | 'SOLO_NOD_CHALLENGE'
  | 'SOLO_SHAKE_CHALLENGE'
  | 'SOLO_BLINK_CHALLENGE';

const transitions: Partial<Record<AppState, Partial<Record<AppEvent, AppState>>>> = {
  landing: {
    ENTER: 'legal-gate',
    SOLO_SCAN: 'solo-scan',
    SOLO_SMILE_SCAN: 'solo-smile-scan',
    SOLO_FROWN_SCAN: 'solo-frown-scan',
    SOLO_ANGER_SCAN: 'solo-anger-scan',
    SOLO_SURPRISE_SCAN: 'solo-surprise-scan',
    SOLO_WINK_SCAN: 'solo-wink-scan',
    SOLO_DISGUST_SCAN: 'solo-disgust-scan',
    SOLO_RESTING_FACE_SCAN: 'solo-resting-face-scan',
    SOLO_STILLNESS_CHALLENGE: 'solo-stillness-challenge',
    SOLO_NOD_CHALLENGE: 'solo-nod-challenge',
    SOLO_SHAKE_CHALLENGE: 'solo-shake-challenge',
    SOLO_BLINK_CHALLENGE: 'solo-blink-challenge',
  },
  'solo-scan': {
    BACK_TO_LANDING: 'landing',
  },
  'solo-smile-scan': {
    BACK_TO_LANDING: 'landing',
  },
  'solo-frown-scan': {
    BACK_TO_LANDING: 'landing',
  },
  'solo-anger-scan': {
    BACK_TO_LANDING: 'landing',
  },
  'solo-surprise-scan': {
    BACK_TO_LANDING: 'landing',
  },
  'solo-wink-scan': {
    BACK_TO_LANDING: 'landing',
  },
  'solo-disgust-scan': {
    BACK_TO_LANDING: 'landing',
  },
  'solo-resting-face-scan': {
    BACK_TO_LANDING: 'landing',
  },
  'solo-stillness-challenge': {
    BACK_TO_LANDING: 'landing',
  },
  'solo-nod-challenge': {
    BACK_TO_LANDING: 'landing',
  },
  'solo-shake-challenge': {
    BACK_TO_LANDING: 'landing',
  },
  'solo-blink-challenge': {
    BACK_TO_LANDING: 'landing',
  },
  'legal-gate': {
    CONSENT_OK: 'permission',
    BACK_TO_LANDING: 'landing',
  },
  permission: {
    PERMISSION_GRANTED: 'calibration',
    CAMERA_DENIED: 'permission-denied',
    BACK_TO_LANDING: 'landing',
  },
  'permission-denied': {
    PERMISSION_GRANTED: 'calibration',
    BACK_TO_LANDING: 'landing',
  },
  calibration: {
    WARMUP_DONE: 'queue',
    POW_FAIL: 'permission',
    BACK_TO_LANDING: 'landing',
  },
  queue: {
    MATCH_FOUND: 'connecting',
    QUEUE_TIMEOUT: 'queue-timeout',
    BACK_TO_LANDING: 'landing',
  },
  'queue-timeout': {
    QUEUE_START: 'queue',
    BACK_TO_LANDING: 'landing',
  },
  connecting: {
    CONNECTED: 'dueling',
    WS_DROPPED: 'peer-dropped',
    BACK_TO_LANDING: 'landing',
  },
  dueling: {
    MATCH_END: 'resolving',
    WS_DROPPED: 'peer-dropped',
    PEER_LEFT: 'resolving',
    REPORT: 'report-flow',
  },
  resolving: {
    RESOLVED: 'resolution',
  },
  resolution: {
    NEXT: 'share',
    BACK_TO_LANDING: 'landing',
  },
  share: {
    NEXT: 'queue',
    BACK_TO_LANDING: 'landing',
  },
  'report-flow': {
    BACK_TO_LANDING: 'landing',
  },
  'peer-dropped': {
    RESOLVED: 'resolution',
    BACK_TO_LANDING: 'landing',
  },
};

interface MachineStore {
  state: AppState;
  send(event: AppEvent): void;
  reset(): void;
}

export const useMachine = create<MachineStore>((set, get) => ({
  state: 'landing',
  send(event) {
    const cur = get().state;
    const next = transitions[cur]?.[event];
    if (next && next !== cur) {
      if (import.meta.env.DEV) {
        console.warn(`[machine] ${cur} -> ${next}  (${event})`);
      }
      set({ state: next });
    }
  },
  reset() {
    set({ state: 'landing' });
  },
}));

export function canTransition(from: AppState, event: AppEvent): boolean {
  return Boolean(transitions[from]?.[event]);
}
